import { tiendaDb } from '../lib/supabase';
// Re-exportar funciones del core para que el visor pueda usarlas
// export { importTicket, reprocessTicket, linkEpelsaProduct } from './core/TicketImportService';

// ─── EPELSA SERVICE ───────────────────────────────────────────────────────────
// Gestión de la integración con la báscula Epelsa dentro del módulo Ventas.
// El agente IA nunca accede a estas tablas directamente.

// ─── CONFIGURACIÓN ────────────────────────────────────────────────────────────
export const epelsaConfigService = {
  get: async () => {
    const { data, error } = await tiendaDb
      ?.from('epelsa_config')
      ?.select('id, nombre, modelo, ip, puerto, protocolo, codigo_bascula, almacen_id, caja_diaria_referencia, recepcion_automatica, activo, ultima_conexion, estado, ultima_sincronizacion, ultimo_ticket_recibido, observaciones, created_at, updated_at, almacen:almacenes(id, nombre)')
      ?.limit(1)
      ?.maybeSingle();
    if (error) throw error;
    return data;
  },

  upsert: async (config) => {
    const { id, almacen, ...rest } = config;
    const payload = { ...rest, updated_at: new Date()?.toISOString() };
    if (id) {
      const { data, error } = await tiendaDb?.from('epelsa_config')?.update(payload)?.eq('id', id)?.select()?.single();
      if (error) throw error;
      return data;
    }
    const { data, error } = await tiendaDb?.from('epelsa_config')?.insert(payload)?.select()?.single();
    if (error) throw error;
    return data;
  },

  setEstado: async (id, estado) => {
    const { data, error } = await tiendaDb?.from('epelsa_config')
      ?.update({ estado, ultima_conexion: new Date()?.toISOString(), updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  toggleRecepcionAutomatica: async (id, valor) => {
    const { data, error } = await tiendaDb?.from('epelsa_config')
      ?.update({ recepcion_automatica: valor, updated_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },
};

// ─── TICKETS LOG ──────────────────────────────────────────────────────────────
export const epelsaTicketsLogService = {
  getAll: async ({ limit = 50, filters = {} } = {}) => {
    let query = tiendaDb
      ?.from('epelsa_tickets_log')
      ?.select('id, numero_ticket, caja, fecha_ticket, estado, venta_id, error_mensaje, procesado_at, created_at, source, lineas_recibidas, lineas_vinculadas, lineas_no_vinculadas, payload, venta:ventas(id, numero_venta, total, forma_pago)');

    if (filters?.estado) query = query?.eq('estado', filters?.estado);
    if (filters?.caja) query = query?.eq('caja', filters?.caja);
    if (filters?.fechaDesde) query = query?.gte('fecha_ticket', filters?.fechaDesde);
    if (filters?.fechaHasta) query = query?.lte('fecha_ticket', filters?.fechaHasta);
    if (filters?.numeroTicket) query = query?.ilike('numero_ticket', `%${filters?.numeroTicket}%`);

    const { data, error } = await query
      ?.order('created_at', { ascending: false })
      ?.limit(limit);
    if (error) throw error;
    return data || [];
  },

  getHoy: async () => {
    const today = new Date()?.toISOString()?.split('T')?.[0];
    const { data, error } = await tiendaDb
      ?.from('epelsa_tickets_log')
      ?.select('id, numero_ticket, estado, created_at, venta_id')
      ?.gte('created_at', `${today}T00:00:00`)
      ?.lte('created_at', `${today}T23:59:59`)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  getErrores: async () => {
    const { data, error } = await tiendaDb
      ?.from('epelsa_tickets_log')
      ?.select('id, numero_ticket, caja, estado, error_mensaje, created_at')
      ?.in('estado', ['ERROR', 'PARCIAL'])
      ?.order('created_at', { ascending: false })
      ?.limit(20);
    if (error) throw error;
    return data || [];
  },

  isDuplicado: async (numero_ticket, caja, fecha_ticket) => {
    const { data, error } = await tiendaDb
      ?.from('epelsa_tickets_log')
      ?.select('id, estado')
      ?.eq('numero_ticket', numero_ticket)
      ?.eq('caja', caja)
      ?.eq('fecha_ticket', fecha_ticket)
      ?.maybeSingle();
    if (error) throw error;
    return data;
  },

  create: async (log) => {
    const { data, error } = await tiendaDb?.from('epelsa_tickets_log')?.insert(log)?.select()?.single();
    if (error) throw error;
    return data;
  },

  update: async (id, updates) => {
    const { data, error } = await tiendaDb?.from('epelsa_tickets_log')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },
};

// ─── LÍNEAS NO VINCULADAS ─────────────────────────────────────────────────────
export const epelsaLineasNoVinculadasService = {
  getAll: async () => {
    const { data, error } = await tiendaDb
      ?.from('epelsa_lineas_no_vinculadas')
      ?.select('id, ticket_log_id, numero_ticket, plu, ean, codigo_interno, nombre_epelsa, peso_kg, precio_kg, importe, iva, estado, producto_id, vinculado_at, vinculado_por, created_at, producto:productos(id, nombre, codigo_interno)')
      ?.eq('estado', 'PENDIENTE')
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  vincular: async (id, productoId, usuario = null) => {
    const { data, error } = await tiendaDb?.from('epelsa_lineas_no_vinculadas')
      ?.update({ estado: 'VINCULADA', producto_id: productoId, vinculado_at: new Date()?.toISOString(), vinculado_por: usuario })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;

    // Guardar vinculación aprendida por EAN y PLU
    if (data?.ean) {
      await tiendaDb?.from('epelsa_vinculaciones')?.upsert(
        { tipo_clave: 'EAN', clave_epelsa: data?.ean, producto_id: productoId, activo: true, updated_at: new Date()?.toISOString() },
        { onConflict: 'tipo_clave,clave_epelsa' }
      );
    }
    if (data?.plu) {
      await tiendaDb?.from('epelsa_vinculaciones')?.upsert(
        { tipo_clave: 'PLU', clave_epelsa: data?.plu, producto_id: productoId, activo: true, updated_at: new Date()?.toISOString() },
        { onConflict: 'tipo_clave,clave_epelsa' }
      );
    }
    if (data?.codigo_interno) {
      await tiendaDb?.from('epelsa_vinculaciones')?.upsert(
        { tipo_clave: 'CODIGO_INTERNO', clave_epelsa: data?.codigo_interno, producto_id: productoId, activo: true, updated_at: new Date()?.toISOString() },
        { onConflict: 'tipo_clave,clave_epelsa' }
      );
    }
    return data;
  },

  ignorar: async (id) => {
    const { data, error } = await tiendaDb?.from('epelsa_lineas_no_vinculadas')
      ?.update({ estado: 'IGNORADA' })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },
};

// ─── VINCULACIONES APRENDIDAS ─────────────────────────────────────────────────
export const epelsaVinculacionesService = {
  buscarProducto: async (ean, plu, nombre) => {
    if (ean) {
      const { data } = await tiendaDb?.from('epelsa_vinculaciones')
        ?.select('producto_id')
        ?.eq('tipo_clave', 'EAN')
        ?.eq('clave_epelsa', ean)
        ?.eq('activo', true)
        ?.maybeSingle();
      if (data?.producto_id) return { productoId: data?.producto_id, metodo: 'EAN_VINCULACION' };
    }
    if (plu) {
      const { data } = await tiendaDb?.from('epelsa_vinculaciones')
        ?.select('producto_id')
        ?.eq('tipo_clave', 'PLU')
        ?.eq('clave_epelsa', plu)
        ?.eq('activo', true)
        ?.maybeSingle();
      if (data?.producto_id) return { productoId: data?.producto_id, metodo: 'PLU_VINCULACION' };
    }
    if (ean) {
      const { data } = await tiendaDb?.from('productos')?.select('id')?.eq('ean', ean)?.eq('activo', true)?.maybeSingle();
      if (data?.id) return { productoId: data?.id, metodo: 'EAN' };
    }
    if (plu) {
      const { data } = await tiendaDb?.from('productos')?.select('id')?.eq('plu', plu)?.eq('activo', true)?.maybeSingle();
      if (data?.id) return { productoId: data?.id, metodo: 'PLU' };
    }
    if (nombre) {
      const { data } = await tiendaDb?.from('productos')?.select('id')?.ilike('nombre', `%${nombre}%`)?.eq('activo', true)?.limit(1);
      if (data?.[0]?.id) return { productoId: data?.[0]?.id, metodo: 'NOMBRE' };
    }
    return null;
  },
};

// ─── PROCESADOR LEGACY (mantener compatibilidad con código existente) ──────────
// Delega al nuevo TicketImportService
export const epelsaTicketProcessor = {
  procesar: async (ticket, _configId) => {
    const { importTicket: coreImport } = await import('./core/TicketImportService');
    return coreImport(ticket, ticket?.source || 'EPELSA');
  },
};

// ─── KPIs EPELSA ──────────────────────────────────────────────────────────────
export const epelsaKpisService = {
  getHoy: async () => {
    const today = new Date()?.toISOString()?.split('T')?.[0];

    const [ticketsHoy, erroresHoy] = await Promise.all([
      tiendaDb?.from('epelsa_tickets_log')
        ?.select('id, estado, created_at')
        ?.gte('created_at', `${today}T00:00:00`)
        ?.lte('created_at', `${today}T23:59:59`),
      tiendaDb?.from('epelsa_tickets_log')
        ?.select('id')
        ?.in('estado', ['ERROR', 'PARCIAL']),
    ]);

    const tickets = ticketsHoy?.data || [];
    const procesados = tickets?.filter(t => t?.estado === 'PROCESADO' || t?.estado === 'PARCIAL');

    const { data: ventasHoy } = await tiendaDb?.from('ventas')
      ?.select('total')
      ?.eq('origen', 'EPELSA')
      ?.gte('fecha', `${today}T00:00:00`)
      ?.lte('fecha', `${today}T23:59:59`);

    const totalVentasHoy = (ventasHoy || [])?.reduce((s, v) => s + (v?.total || 0), 0);

    return {
      ticketsHoy: tickets?.length,
      ticketsProcesados: procesados?.length,
      ticketsDuplicados: tickets?.filter(t => t?.estado === 'DUPLICADO')?.length,
      ventasHoy: totalVentasHoy,
      erroresIntegracion: erroresHoy?.data?.length || 0,
    };
  },
};
